/**
 * Agapella Design System — Color Tokens
 *
 * Canonical source: docs/agapella-design-system/project/colors_and_type.css
 * (cross-validated against this palette in 2026-05-21; see refactor-workflow.md).
 *
 * Aesthetic: warm, photo-led, monastic chic. One accent (Solar Orange). Lots of
 * white space. Cards float on warm-brown shadows, not gray.
 *
 * Two near-blacks intentionally:
 *  - obsidian (#00121D)  — primary text + dark CTAs       (fg-1 / official)
 *  - charcoal (#0F1419)  — alt near-black used in surfaces (Card text in dark scenes)
 * They look almost identical at glance but separate roles cleanly.
 */

export const colors = {
  // ── Core surfaces ────────────────────────────────────────────
  canvas: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceElevated: '#FFFFFF',
  /** Subtle warm block — chat bubble (other), agapē banner backing. */
  surfaceWarm: '#FAFAF8',
  /** Soft tint — secondary blocks, agapē practice banner. Same value as
   *  `backgroundSoft` — both ship so existing call sites don't churn. */
  surfaceSoft: '#F4F7F6',
  backgroundSoft: '#F4F7F6',
  backgroundAlt: '#F1DAC3',     // Official Warm Beige (community sections, alt)
  cream: '#F9F7F4',             // warm off-white
  sand: '#E8E6E1',              // dusty sand

  // ── Near-blacks (two roles, both ship) ───────────────────────
  obsidian: '#00121D',          // primary text + dark CTAs (canonical fg-1)
  charcoal: '#0F1419',          // alt near-black for surfaces
  brown: '#1E3A5F',
  gray: '#949391',
  white: '#FFFFFF',
  black: '#000000',

  // ── Solar Orange — the single brand accent ───────────────────
  /** ★ The one orange — every CTA, active state, brand mark. */
  solar: '#E98245',
  solarHover: '#D97338',
  solarPress: '#C9652F',
  solarDeep: '#9A2B00',
  ctaStart: '#F45122',
  ctaEnd: '#FF955D',
  ctaHoverStart: '#E94A1D',
  ctaHoverEnd: '#FF864B',
  ctaPressedStart: '#D94318',
  ctaPressedEnd: '#F4773E',
  fireStart: '#FF7020',         // logo gradient start
  fireEnd: '#FFD1A0',           // logo gradient end

  // ── Semantic ─────────────────────────────────────────────────
  error: '#E53E3E',
  success: '#38A169',
  warning: '#D69E2E',
  info: '#3182CE',
  link: '#3B82F6',
  starGold: '#FBBF24',
  onlineGreen: '#22C55E',
  offlineGray: '#A8A29E',

  // ── Text roles (semantic aliases; tied to obsidian/fg-2/fg-3) ─
  textMain: '#00121D',          // primary text (= obsidian)
  textBody: '#4A453F',          // body — warm mid-tone (fg-2)
  textSecondary: '#4A453F',     // alias
  textMuted: '#8A877F',         // muted / caption (fg-3)

  // ── Legacy aliases — keep so existing call sites don't break ─
  fire: '#E98245',              // == solar
  peach: '#E98245',             // == solar

  // ── Extended palette — brand-aligned solar shades ────────────
  orange50: '#FFF5EE',
  orange100: '#FFE8D6',
  orange200: '#FFD0AA',
  orange400: '#FF7A3D',
  orange500: '#E98245',
  orange600: '#D97338',
  orange700: '#C9652F',
  orange800: '#9A2B00',
  red50: '#fef2f2',
  red600: '#dc2626',
  red700: '#b91c1c',
  blue600: '#2563eb',
  green600: '#16a34a',
  purple600: '#9333ea',
  slate50: '#f8fafc',
  slate100: '#f1f5f9',
  slate200: '#e2e8f0',
  slate300: '#cbd5e1',
  slate400: '#94a3b8',
  slate500: '#64748b',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1e293b',
  slate900: '#0f172a',
};

/**
 * Brand gradients — used sparingly. `agape` is the logo gradient
 * (orange → soft sand); everything else stays flat.
 */
export const gradients = {
  cta: 'linear-gradient(90deg, #F45122 0%, #FF955D 100%)',
  ctaHover: 'linear-gradient(90deg, #E94A1D 0%, #FF864B 100%)',
  ctaPressed: 'linear-gradient(90deg, #D94318 0%, #F4773E 100%)',
  fire: 'linear-gradient(135deg, #FF7020 0%, #FFD1A0 100%)',
  solar: 'linear-gradient(135deg, #E98245 0%, #1E3A5F 100%)',
  grounded: 'linear-gradient(160deg, #00121D 0%, #1E3A5F 100%)',
  clarity:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.2) 100%)',
  agape: 'linear-gradient(160deg, #00121D 0%, #C2651F 50%, #E98245 100%)',
  warm: 'linear-gradient(135deg, #FFFFFF 0%, #F9F7F4 30%, #FF7A3D 100%)',
  text: 'linear-gradient(to right, #E98245, #C9652F)',
  /** Photo card protection — text legible against full-bleed imagery. */
  overlayCard:
    'linear-gradient(to top, rgba(0,10,18,0.92) 0%, rgba(0,10,18,0.5) 50%, transparent 100%)',
  overlayProtect:
    'linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.4) 100%)',
};

export type ColorKey = keyof typeof colors;
export type GradientKey = keyof typeof gradients;
